import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookMarked, NotebookPen, Users, Sailboat, ImageIcon } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, RetellingBadge, ScriptureRef } from "@/components/cast/Common";
import { SeasonalHero } from "@/components/cast/SeasonalHero";
import { StreakCard } from "@/components/cast/Streak";
import { VerseCardDialog } from "@/components/cast/VerseCard";
import { ChurchSeason } from "@/components/cast/ChurchSeason";

export default function Shore() {
  const { user } = useAuth();
  const [d, setD] = useState<any>(null);
  const [card, setCard] = useState(false);
  useEffect(() => { api.get("/shore").then((r) => setD(r.data)); }, []);
  const verseCard = useMemo(() => d && { ref: d.verse.ref, text: d.verse.text, version: d.verse.abbr || "KJV", eyebrow: "VERSE OF THE DAY" }, [d]);
  if (!d) return <Loader />;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div className="space-y-14">
      <ChurchSeason kids />
      <SeasonalHero compact>
        <div className="grid items-end gap-8 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-amber-200" data-testid="page-eyebrow">Shore · Home & Daily Devotion</p>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="mt-3 font-display text-4xl font-semibold tracking-tight text-white drop-shadow-lg sm:text-5xl lg:text-6xl" data-testid="shore-greeting">{greet}, {user.username}.</motion.h1>
          </div>
          <motion.section initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }} className="rounded-2xl border border-white/20 bg-slate-900/35 p-6 text-amber-50 backdrop-blur-md lg:col-span-6" data-testid="verse-of-day">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-200">Verse for today</p>
            <p className="mt-3 font-scripture text-xl italic leading-relaxed sm:text-2xl">“{d.verse.text}”</p>
            <p className="mt-3 text-sm font-semibold text-amber-200">{d.verse.ref} · {d.verse.abbr || "KJV"}</p>
            <Link to={`/deep-water?tab=ask&q=${encodeURIComponent(`Help me reflect on ${d.verse.ref}`)}`} data-testid="verse-reflect-link" className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-50/10 px-4 py-2 text-sm font-semibold text-amber-50 ring-1 ring-amber-50/30 hover:bg-amber-50/20">Reflect with the study helper <ArrowRight className="h-4 w-4" /></Link>
            <button onClick={() => setCard(true)} data-testid="verse-make-card" className="ml-2 mt-5 inline-flex items-center gap-2 rounded-full bg-amber-300 px-4 py-2 text-sm font-semibold text-amber-950 transition-transform hover:scale-105"><ImageIcon className="h-4 w-4" />Make a card</button>
            {card && <VerseCardDialog open={card} onOpenChange={setCard} verse={verseCard} />}
          </motion.section>
        </div>
      </SeasonalHero>

      <StreakCard verse={d.verse} cont={d.continue} />

      <div className="grid gap-6 lg:grid-cols-12">
        <div className="contents">
          <section className="card-warm p-7 lg:col-span-7" data-testid="devotion-today-card">
            <p className="eyebrow">Today's devotion</p>
            {d.devotion?.day ? (
              <>
                <h3 className="mt-3 font-display text-2xl font-semibold text-slate-900">Day {d.devotion.day.day}: {d.devotion.day.title}</h3>
                <p className="mt-1 text-sm text-slate-600">{d.devotion.plan_title} · {d.devotion.done}/{d.devotion.total} done</p>
                <div className="mt-3"><ScriptureRef refText={d.devotion.day.passage_ref} /></div>
                <Link to="/deep-water?tab=plans" data-testid="devotion-open-link" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#C86D51]">Open plan <ArrowRight className="h-4 w-4" /></Link>
              </>
            ) : (
              <>
                <p className="mt-3 text-slate-700">{d.devotion ? "You've finished your plan. Well done." : "Build a personal devotion plan around a topic you care about."}</p>
                <Link to="/deep-water?tab=plans" data-testid="devotion-create-link" className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#C86D51]">Create a plan <ArrowRight className="h-4 w-4" /></Link>
              </>
            )}
          </section>
          <section className="grid grid-cols-3 gap-3 self-start lg:col-span-5" data-testid="shore-stats">
            {[{ i: Sailboat, n: d.stats.stories, l: "stories done" }, { i: NotebookPen, n: d.stats.notes, l: "notes" }, { i: Users, n: d.stats.groups, l: "groups" }].map((s) => (
              <div key={s.l} className="card-warm p-4 text-center"><s.i className="mx-auto h-5 w-5 text-[#5B7065]" /><p className="mt-2 font-display text-3xl font-semibold">{s.n}</p><p className="text-xs text-slate-500">{s.l}</p></div>
            ))}
          </section>
        </div>
      </div>

      {d.continue && (
        <Link to={`/episode/${d.continue.id}`} data-testid="continue-episode-card" className="group flex flex-col overflow-hidden rounded-3xl border border-[#E2DBCE] bg-white transition-shadow hover:shadow-lg sm:flex-row">
          <img src={d.continue.cover} alt="" className="h-44 w-full object-cover sm:h-auto sm:w-72" />
          <div className="flex flex-1 flex-col justify-center p-7">
            <p className="eyebrow">Continue your journey</p>
            <h3 className="mt-2 font-display text-3xl font-semibold text-slate-900">{d.continue.title}</h3>
            <p className="text-sm text-slate-600">{d.continue.series} · {d.continue.ref}</p>
            <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#C86D51] group-hover:gap-2 transition-all">Resume <ArrowRight className="h-4 w-4" /></span>
          </div>
        </Link>
      )}

      <section>
        <div className="mb-6 flex items-end justify-between">
          <div><p className="eyebrow">Boat · Story Journeys</p><h2 className="mt-2 font-display text-3xl font-semibold text-slate-900">Featured stories</h2></div>
          <Link to="/boat" data-testid="shore-all-stories-link" className="text-sm font-semibold text-[#C86D51]">All stories →</Link>
        </div>
        <div className="grid gap-6 md:grid-cols-3">
          {d.featured.map((s: any, i: number) => (
            <motion.div key={s.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}>
              <Link to={`/boat/${s.id}`} data-testid={`featured-series-${s.id}`} className="group block overflow-hidden rounded-2xl border border-[#E2DBCE] bg-white transition-all hover:-translate-y-1 hover:shadow-lg">
                <div className="overflow-hidden"><img src={s.cover} alt={s.title} className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
                <div className="space-y-2 p-5"><RetellingBadge />
                  <h3 className="font-display text-2xl font-semibold text-slate-900">{s.title}</h3>
                  <p className="flex items-center gap-1.5 text-sm text-slate-500"><BookMarked className="h-4 w-4" />{s.book} · {s.episodes.length} episodes</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
